import React, { FunctionComponent } from 'react'
import { View } from 'react-native'

import { useTheme } from 'styled-components/native'

import {
  MicrositeConfig,
  MicrositeConfigSectionNames,
} from '@lyrahealth-inc/shared-app-logic/src/models/customer/Customer'

import { ThemeType } from '../../../utils/themes/ThemeProvider'
import { tID } from '../../../utils/utils'
import { useGetMicrositeSections } from '../../hooks/useGetMicrositeSections'
import { AuroraSectionDivider } from '../../organisms/auroraSectionDivider/AuroraSectionDivider'
import { AuroraSectionGradientWrapper } from '../../organisms/auroraSectionGradientWrapper/AuroraSectionGradientWrapper'
import {
  LegalAndCopyrightFooter,
  LegalAndCopyrightFooterProps,
} from '../../organisms/legalAndCopyrightFooter/LegalAndCopyrightFooter'
import { LyraMembershipSummaryProps } from '../../organisms/lyraMembershipSummary/LyraMembershipSummary'
import { LyraOfferingsCarouselProps } from '../../organisms/lyraOfferingsCarousel/LyraOfferingsCarousel'
import { MicrositeMobileAppPromoSectionProps } from '../../organisms/micrositeMobileAppPromo/MicrositeMobileAppPromoSection'
import { PathwaysCareNavigatorSectionProps } from '../../organisms/pathwaysCareNavigatorSection/PathwaysCareNavigatorSection'
import { PathwaysFAQSectionProps } from '../../organisms/pathwaysFAQSection/PathwaysFAQSection'
import { PathwaysHomepageHeroSectionProps } from '../../organisms/PathwaysHomepageHeroSection/PathwaysHomepageHeroSection'
import { PathwaysSelfCareSectionProps } from '../../organisms/pathwaysSelfCareSection/PathwaysSelfCareSection'

export interface ComingSoonMicrositeHomePageProps {
  heroSectionProps: Pick<
    PathwaysHomepageHeroSectionProps,
    | 'pathwaysType'
    | 'startDate'
    | 'onNotifyMeOnLaunchPress'
    | 'onExploreOnYourOwnCTAPress'
    | 'isRegistrationAndLoginDisabled'
  >
  lyraOfferingsSectionProps: Pick<
    LyraOfferingsCarouselProps,
    | 'customerPropertiesMap'
    | 'pathwaysType'
    | 'isInternational'
    | 'meetWithAnExpertTabPress'
    | 'wellnessResourcesTabPress'
    | 'lyraOfferingsTabPress'
  >
  selfCareSectionProps: Pick<PathwaysSelfCareSectionProps, 'language' | 'pathwaysType' | 'onContentClicked'>
  careNavigatorSectionProps: Pick<PathwaysCareNavigatorSectionProps, 'pathwaysType'>
  faqSectionProps: Pick<
    PathwaysFAQSectionProps,
    | 'membershipType'
    | 'beforeLaunch'
    | 'startDate'
    | 'hasMembershipInCustomer'
    | 'onNotifyMeOnLaunchPress'
    | 'onQuestionPress'
    | 'customerCostProperties'
    | 'isCostMessagingEnabled'
    | 'isRegistrationAndLoginDisabled'
  >
  legalAndCopyrightFooterProps: LegalAndCopyrightFooterProps
  membershipSectionProps: LyraMembershipSummaryProps
  mobileAppPromoSectionProps: MicrositeMobileAppPromoSectionProps
  micrositeConfig: MicrositeConfig
}

export const ComingSoonMicrositeHomePage: FunctionComponent<ComingSoonMicrositeHomePageProps> = ({
  heroSectionProps,
  lyraOfferingsSectionProps,
  selfCareSectionProps,
  careNavigatorSectionProps,
  faqSectionProps,
  legalAndCopyrightFooterProps,
  membershipSectionProps,
  mobileAppPromoSectionProps,
  micrositeConfig,
}) => {
  const sectionToPropsMap = {
    [MicrositeConfigSectionNames.HERO]: heroSectionProps,
    [MicrositeConfigSectionNames.OFFERINGS_CAROUSEL]: {
      ...lyraOfferingsSectionProps,
      ...micrositeConfig[MicrositeConfigSectionNames.OFFERINGS_CAROUSEL],
    },
    [MicrositeConfigSectionNames.MOBILE_APP_PROMO]: mobileAppPromoSectionProps,
    [MicrositeConfigSectionNames.SELF_CARE]: selfCareSectionProps,
    [MicrositeConfigSectionNames.TESTIMONIALS]: {},
    [MicrositeConfigSectionNames.LYRA_MEMBERSHIP]: membershipSectionProps,
    [MicrositeConfigSectionNames.CARE_NAVIGATOR]: careNavigatorSectionProps,
    [MicrositeConfigSectionNames.FAQ]: faqSectionProps,
  }

  const sectionsDisplayed = useGetMicrositeSections(micrositeConfig) as Array<{
    name: Exclude<
      MicrositeConfigSectionNames,
      MicrositeConfigSectionNames.MOBILE_APP_PROMO_BANNER | MicrositeConfigSectionNames.CUSTOMER_BRANDED_BANNER
    >
    component: FunctionComponent
    backgroundColor?: string
    useAuroraGradient?: boolean
  }>
  const { isAuroraTheme } = useTheme() as ThemeType

  const sectionsWithDividerAfter = new Set([
    MicrositeConfigSectionNames.OFFERINGS_CAROUSEL,
    MicrositeConfigSectionNames.MOBILE_APP_PROMO,
    MicrositeConfigSectionNames.SELF_CARE,
    MicrositeConfigSectionNames.TESTIMONIALS,
    MicrositeConfigSectionNames.LYRA_MEMBERSHIP,
  ])

  const renderSection = (_sectionName: string, Component: React.ComponentType, sectionProps: object) => {
    return <Component {...sectionProps} />
  }

  const renderSectionWithDivider = (section: (typeof sectionsDisplayed)[0]) => {
    const { name: sectionName, component: Component, backgroundColor } = section
    const sectionProps = { ...sectionToPropsMap[sectionName], ...(backgroundColor && { backgroundColor }) }
    const showDivider = isAuroraTheme && sectionsWithDividerAfter.has(sectionName)

    return (
      <React.Fragment key={sectionName}>
        {renderSection(sectionName, Component, sectionProps)}
        {showDivider && <AuroraSectionDivider />}
      </React.Fragment>
    )
  }

  // Find the indices to split sections
  const firstGradientIndex = sectionsDisplayed.findIndex((s) => s.useAuroraGradient)
  const lastGradientIndex = sectionsDisplayed.map((s) => s.useAuroraGradient).lastIndexOf(true)

  // Sections before the gradient (e.g., Hero)
  const sectionsBeforeGradient = firstGradientIndex > 0 ? sectionsDisplayed.slice(0, firstGradientIndex) : []

  // Sections inside the gradient
  const gradientSections =
    firstGradientIndex >= 0 ? sectionsDisplayed.slice(firstGradientIndex, lastGradientIndex + 1) : []

  // Sections after the gradient (e.g., FAQ)
  const sectionsAfterGradient =
    lastGradientIndex >= 0 && lastGradientIndex < sectionsDisplayed.length - 1
      ? sectionsDisplayed.slice(lastGradientIndex + 1)
      : []

  // When Aurora is not enabled, render all sections normally
  if (!isAuroraTheme) {
    return (
      <View testID={tID('ComingSoonMicrositeHomePage')}>
        {sectionsDisplayed.map(renderSectionWithDivider)}
        <LegalAndCopyrightFooter {...legalAndCopyrightFooterProps} />
      </View>
    )
  }

  return (
    <View testID={tID('ComingSoonMicrositeHomePage')}>
      {/* Render sections before gradient (e.g., Hero) */}
      {sectionsBeforeGradient.map(renderSectionWithDivider)}

      {/* Wrap all gradient sections in a single gradient wrapper */}
      {isAuroraTheme && gradientSections.length > 0 ? (
        <AuroraSectionGradientWrapper>{gradientSections.map(renderSectionWithDivider)}</AuroraSectionGradientWrapper>
      ) : (
        gradientSections.map(renderSectionWithDivider)
      )}

      {/* Render sections after gradient (e.g., FAQ) */}
      {sectionsAfterGradient.map(renderSectionWithDivider)}

      {/* Footer at the bottom */}
      <LegalAndCopyrightFooter {...legalAndCopyrightFooterProps} />
    </View>
  )
}
